import { useEffect, useRef, useState } from "react";

export interface PresenceSnapshot {
  viewers: number;
  flashing: number;
  totalVisits: number;
  totalFlashes: number;
}

const HEARTBEAT_INTERVAL_MS = 10_000;

function createClientId(): string {
  try {
    const existing = window.sessionStorage.getItem("usb-workbench-presence-id");
    if (existing) return existing;
    const generated = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem("usb-workbench-presence-id", generated);
    return generated;
  } catch {
    return globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

async function sendPresence(clientId: string, flashing: boolean, active: boolean): Promise<PresenceSnapshot | undefined> {
  try {
    const response = await fetch("/__presence", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clientId, flashing, active }),
      keepalive: !active,
    });
    if (!response.ok) return undefined;
    const payload = await response.json() as Partial<PresenceSnapshot>;
    if (!Number.isFinite(payload.viewers) || !Number.isFinite(payload.flashing) || !Number.isFinite(payload.totalVisits) || !Number.isFinite(payload.totalFlashes)) return undefined;
    return {
      viewers: Math.max(0, Math.floor(payload.viewers ?? 0)),
      flashing: Math.max(0, Math.floor(payload.flashing ?? 0)),
      totalVisits: Math.max(0, Math.floor(payload.totalVisits ?? 0)),
      totalFlashes: Math.max(0, Math.floor(payload.totalFlashes ?? 0)),
    };
  } catch {
    // Presence is informational; a disconnected server must not affect USB work.
    return undefined;
  }
}

/** Publishes a short-lived browser session heartbeat and returns server totals. */
export function usePresence(flashing: boolean): PresenceSnapshot | undefined {
  const clientId = useRef<string | undefined>(undefined);
  const [snapshot, setSnapshot] = useState<PresenceSnapshot>();

  if (!clientId.current) clientId.current = createClientId();

  useEffect(() => {
    let disposed = false;
    const id = clientId.current!;
    const heartbeat = async () => {
      const next = await sendPresence(id, flashing, true);
      if (!disposed && next) setSnapshot(next);
    };
    const leave = () => {
      void sendPresence(id, false, false);
    };

    void heartbeat();
    const timer = window.setInterval(() => void heartbeat(), HEARTBEAT_INTERVAL_MS);
    window.addEventListener("pagehide", leave);
    return () => {
      disposed = true;
      window.clearInterval(timer);
      window.removeEventListener("pagehide", leave);
    };
  }, [flashing]);

  return snapshot;
}
